import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { common_lang_get } from '@/server/modules/3426.js'
import {
  appTab_config_publishedDetail_get,
  appTab_config_publish_post,
  appTab_experiment_config_publish_post,
  appTab_experiment_version_page_post,
  appTab_material_enabledCandidates_post,
  appTab_material_update_post,
  appTab_version_page_post
} from '@/server/modules/25824.js'
import {
  buildPublishPayload,
  cloneConfig,
  createDraft,
  createVariant,
  describeDraftChanges,
  filterCandidates,
  getDefaultVariant,
  getLatestVersionNo,
  getMaterialLanguage,
  getMaterialName as materialName,
  setExperimentMode,
  updateSharedMaterial,
  validateDraft,
  validateVariantInfo
} from './configDraft'

export default function useTabConfiguration(props) {
  const draft = ref(createDraft())
  const publishedDraft = ref(cloneConfig(draft.value))
  const publishedDetail = ref(null)
  const activeVariantId = ref(draft.value.variants[0].uiId)
  const activeVariant = computed(() =>
    draft.value.variants.find(item => item.uiId === activeVariantId.value)
  )
  const variantField = (field, fallback) =>
    computed({
      get: () => activeVariant.value?.[field] ?? fallback,
      set: value => {
        if (activeVariant.value) activeVariant.value[field] = value
      }
    })
  const items = variantField('items', [])
  const defaultMaterialNo = variantField('defaultMaterialNo', '')
  const previewMaterialNo = variantField('previewMaterialNo', '')
  const expectedVersions = ref({ 0: null, 1: null })
  const configLoading = ref(false)
  const configReady = ref(false)
  const configError = ref('')
  const baselineError = ref('')
  const baselineLoading = ref(false)
  const publishModalVisible = ref(false)
  const publishLoading = ref(false)
  const candidateItems = ref([])
  const candidateLoading = ref(false)
  const candidateModalVisible = ref(false)
  const modalVisible = ref(false)
  const editingRecord = ref(null)
  const submitLoading = ref(false)
  const languageLoading = ref(false)
  const languageOptions = ref([])
  const defaultLanguageCode = ref('')
  const languageSiteId = ref()
  const experimentPanelRef = ref()
  const validationError = ref(null)
  const variantModalVisible = ref(false)
  const editingVariant = ref(null)
  const interactionDisabled = computed(
    () =>
      !configReady.value ||
      configLoading.value ||
      publishLoading.value ||
      submitLoading.value
  )
  const currentVersion = computed(() =>
    Number(publishedDetail.value?.versionNo) > 0
      ? publishedDetail.value.versionName || ''
      : ''
  )
  const remainingSlots = computed(() => Math.max(0, 5 - items.value.length))
  const getMaterialName = item => materialName(item, defaultLanguageCode.value)
  const getMaterialDisplayText = item =>
    getMaterialLanguage(item, defaultLanguageCode.value)?.displayText || '-'
  const publishChanges = computed(() =>
    describeDraftChanges(
      publishedDraft.value,
      draft.value,
      defaultLanguageCode.value
    )
  )
  const publishChecks = computed(() => [
    {
      text: `至少装配 3 个 Tab（当前 ${items.value.length} 个）`,
      valid: items.value.length >= 3
    },
    {
      text: `最多装配 5 个 Tab（当前 ${items.value.length} 个）`,
      valid: items.value.length <= 5
    },
    {
      text: `大图类型最多 1 个（当前 ${
        items.value.filter(item => Number(item.materialType) === 2).length
      } 个）`,
      valid:
        items.value.filter(item => Number(item.materialType) === 2).length <= 1
    },
    {
      text: '恰好设置 1 个默认选中 Tab',
      valid: items.value.some(
        item => item.materialNo === defaultMaterialNo.value
      )
    }
  ])

  let generation = 0
  let candidateRequest = 0
  let languagePromise = null
  let confirmation = null
  const context = () => ({ generation, siteId: props.siteId })
  const isCurrent = ctx =>
    ctx.generation === generation && ctx.siteId === props.siteId
  const closeEditors = () => {
    candidateRequest++
    candidateLoading.value = false
    candidateModalVisible.value = false
    candidateItems.value = []
    modalVisible.value = false
    editingRecord.value = null
    variantModalVisible.value = false
    editingVariant.value = null
  }

  const loadOtherVersion = async (ctx = context()) => {
    if (!isCurrent(ctx) || !publishedDetail.value || baselineLoading.value)
      return
    const otherMode =
      Number(publishedDetail.value.experimentEnabled) === 1 ? 0 : 1
    const request = otherMode
      ? appTab_experiment_version_page_post
      : appTab_version_page_post
    baselineLoading.value = true
    baselineError.value = ''
    try {
      const { success, data, retInfo } = await request({
        siteId: Number(ctx.siteId),
        pageNum: 1,
        pageSize: 1
      })
      if (!isCurrent(ctx)) return
      if (!success) throw new Error(retInfo || '另一模式的发布版本获取失败')
      expectedVersions.value[otherMode] = getLatestVersionNo(data)
    } catch (error) {
      if (isCurrent(ctx))
        baselineError.value = error?.message || '另一模式的发布版本获取失败'
    } finally {
      if (isCurrent(ctx)) baselineLoading.value = false
    }
  }

  const loadPublishedConfig = async () => {
    const ctx = context()
    if (!ctx.siteId || configLoading.value) return
    configLoading.value = true
    configReady.value = false
    configError.value = ''
    expectedVersions.value = { 0: null, 1: null }
    try {
      const {
        success,
        data,
        retInfo
      } = await appTab_config_publishedDetail_get({ siteId: ctx.siteId })
      if (!isCurrent(ctx)) return
      if (!success) throw new Error(retInfo || '最新发布配置获取失败')
      if (
        !data ||
        data.versionNo == null ||
        !Number.isInteger(Number(data.versionNo)) ||
        Number(data.versionNo) < 0
      )
        throw new Error('已发布配置版本数据异常，请重试')
      const loadedDraft = createDraft(data)
      if (!getDefaultVariant(loadedDraft))
        throw new Error('已发布配置缺少默认变体，请联系管理员')
      draft.value = loadedDraft
      publishedDraft.value = cloneConfig(loadedDraft)
      publishedDetail.value = cloneConfig(data)
      activeVariantId.value = getDefaultVariant(loadedDraft).uiId
      expectedVersions.value[loadedDraft.experimentEnabled] = Number(
        data.versionNo
      )
      configReady.value = true
      validationError.value = null
      await loadOtherVersion(ctx)
    } catch (error) {
      if (isCurrent(ctx))
        configError.value = error?.message || '最新发布配置获取失败'
    } finally {
      if (isCurrent(ctx)) configLoading.value = false
    }
  }

  const loadLanguageOptions = () => {
    if (!props.siteId) return Promise.resolve(false)
    if (languageOptions.value.length && languageSiteId.value === props.siteId)
      return Promise.resolve(true)
    if (languagePromise) return languagePromise
    const ctx = context()
    languageLoading.value = true
    languagePromise = (async () => {
      try {
        const { success, data, retInfo } = await common_lang_get({
          brandId: 1,
          channelId: 1,
          siteId: ctx.siteId,
          terminalId: 2
        })
        if (!isCurrent(ctx)) return false
        if (!success) throw new Error(retInfo || '站点语言获取失败')
        languageOptions.value = Object.entries(
          data?.languageMap || {}
        ).map(([label, value]) => ({ label, value }))
        defaultLanguageCode.value =
          data?.defaultLanguageCode || languageOptions.value[0]?.value || ''
        languageSiteId.value = ctx.siteId
        if (!languageOptions.value.length)
          throw new Error('当前站点暂无可用语言')
        return true
      } catch (error) {
        if (isCurrent(ctx)) message.error(error?.message || '站点语言获取失败')
        return false
      } finally {
        if (isCurrent(ctx)) {
          languageLoading.value = false
          languagePromise = null
        }
      }
    })()
    return languagePromise
  }

  const selectVariant = uiId => {
    if (
      interactionDisabled.value ||
      !draft.value.variants.some(item => item.uiId === uiId)
    )
      return
    closeEditors()
    activeVariantId.value = uiId
  }
  const handleExperimentToggle = enabled => {
    if (interactionDisabled.value) return
    const ctx = context()
    const applyMode = () => {
      if (!isCurrent(ctx) || interactionDisabled.value) return
      if (!setExperimentMode(draft.value, enabled)) return
      closeEditors()
      activeVariantId.value = getDefaultVariant(draft.value).uiId
      validationError.value = null
    }
    if (enabled) applyMode()
    else
      confirmation = Modal.confirm({
        title: '确认关闭实验？',
        content:
          '将仅保留默认配置的 Tab 列表和默认选中项，清除其他变体及实验信息。发布后才会改变线上配置。',
        okText: '确认关闭',
        cancelText: '取消',
        onOk: applyMode
      })
  }
  const handleExperimentField = ({ field, value }) => {
    if (
      interactionDisabled.value ||
      !['experimentId', 'experimentKey', 'defaultVariantKey'].includes(field)
    )
      return
    draft.value[field] = value
    if (field === 'defaultVariantKey')
      getDefaultVariant(draft.value).variantKey = value
    validationError.value = null
  }
  const openVariantModal = (variant = null) => {
    if (interactionDisabled.value) return
    editingVariant.value = variant ? cloneConfig(variant) : null
    variantModalVisible.value = true
  }
  const handleVariantConfirm = form => {
    if (interactionDisabled.value) return
    const error = validateVariantInfo(form, draft.value.variants)
    if (error) {
      message.warning(error.message)
      return
    }
    const existing = draft.value.variants.find(item => item.uiId === form.uiId)
    if (existing)
      Object.assign(existing, {
        variantName: form.variantName,
        variantKey: form.variantKey
      })
    else {
      const variant = createVariant(form)
      draft.value.variants.push(variant)
      selectVariant(variant.uiId)
    }
    validationError.value = null
    variantModalVisible.value = false
  }
  const removeVariant = variant => {
    if (interactionDisabled.value || variant.isDefault === 1) return
    const ctx = context()
    confirmation = Modal.confirm({
      title: `删除变体“${variant.variantName}”？`,
      content: '仅删除当前草稿中的变体，不会删除物料库中的物料。',
      okText: '删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        if (!isCurrent(ctx) || interactionDisabled.value) return
        draft.value.variants = draft.value.variants.filter(
          item => item.uiId !== variant.uiId
        )
        if (activeVariantId.value === variant.uiId)
          selectVariant(getDefaultVariant(draft.value).uiId)
        validationError.value = null
      }
    })
  }

  const openEditModal = async record => {
    if (interactionDisabled.value) return
    const ctx = context()
    const variantId = activeVariantId.value
    if (
      !(await loadLanguageOptions()) ||
      !isCurrent(ctx) ||
      variantId !== activeVariantId.value
    )
      return
    editingRecord.value = cloneConfig(record)
    modalVisible.value = true
  }
  const openCandidateModal = async () => {
    if (interactionDisabled.value || candidateLoading.value) return
    const ctx = context()
    const variantId = activeVariantId.value
    const requestId = ++candidateRequest
    const valid = () =>
      isCurrent(ctx) &&
      requestId === candidateRequest &&
      variantId === activeVariantId.value
    candidateLoading.value = true
    try {
      const {
        success,
        data,
        retInfo
      } = await appTab_material_enabledCandidates_post({ siteId: ctx.siteId })
      if (!valid()) return
      if (!success) throw new Error(retInfo || '可装配 Tab 获取失败')
      candidateItems.value = filterCandidates(data, items.value)
      candidateModalVisible.value = true
    } catch (error) {
      if (valid()) message.error(error?.message || '可装配 Tab 获取失败')
    } finally {
      if (valid()) candidateLoading.value = false
    }
  }
  const handleAddCandidates = selectedMaterials => {
    if (interactionDisabled.value || !candidateModalVisible.value) return
    const additions = filterCandidates(selectedMaterials, items.value).slice(
      0,
      remainingSlots.value
    )
    items.value = [...items.value, ...cloneConfig(additions)]
    if (!previewMaterialNo.value)
      previewMaterialNo.value = items.value[0]?.materialNo || ''
    candidateModalVisible.value = false
    message.success(`已添加 ${additions.length} 个 Tab`)
  }
  const handleRemove = item => {
    if (interactionDisabled.value) return
    items.value = items.value.filter(
      current => current.materialNo !== item.materialNo
    )
    if (defaultMaterialNo.value === item.materialNo)
      defaultMaterialNo.value = ''
    if (previewMaterialNo.value === item.materialNo)
      previewMaterialNo.value =
        defaultMaterialNo.value || items.value[0]?.materialNo || ''
  }
  const handleSetDefault = item => {
    if (!interactionDisabled.value) defaultMaterialNo.value = item.materialNo
  }
  const handlePreviewSelect = item => {
    previewMaterialNo.value = item.materialNo
  }

  const checkPublish = () => {
    if (interactionDisabled.value) return false
    if (expectedVersions.value[draft.value.experimentEnabled] === null) {
      message.warning('当前发布模式的版本基准尚未获取，请先重试获取版本基准')
      return false
    }
    const error = validateDraft(draft.value)
    validationError.value = error
    if (!error) return true
    if (error.variantId) selectVariant(error.variantId)
    if (
      error.variantId === getDefaultVariant(draft.value)?.uiId &&
      error.field === 'variantKey'
    )
      error.field = 'defaultVariantKey'
    nextTick(() => experimentPanelRef.value?.focusField(error.field))
    message.warning(error.message)
    return false
  }
  const handleOpenPublish = () => {
    if (checkPublish()) publishModalVisible.value = true
  }
  const handlePublish = async releaseNote => {
    if (!checkPublish()) return
    if (!releaseNote?.trim() || releaseNote.trim().length > 1000) {
      message.warning('请输入不超过 1000 字符的版本说明')
      return
    }
    const ctx = context()
    const request = draft.value.experimentEnabled
      ? appTab_experiment_config_publish_post
      : appTab_config_publish_post
    const payload = buildPublishPayload(
      draft.value,
      ctx.siteId,
      expectedVersions.value[draft.value.experimentEnabled],
      releaseNote
    )
    publishLoading.value = true
    try {
      const { success, retInfo } = await request(payload)
      if (!isCurrent(ctx)) return
      if (!success) throw new Error(retInfo || '发布失败')
      publishModalVisible.value = false
      message.success('发布成功')
      await loadPublishedConfig()
    } catch (error) {
      if (isCurrent(ctx)) message.error(error?.message || '发布失败')
    } finally {
      if (isCurrent(ctx)) publishLoading.value = false
    }
  }
  const handleSubmit = async form => {
    if (interactionDisabled.value || !editingRecord.value?.materialNo) return
    const ctx = context()
    const originalRecord = cloneConfig(editingRecord.value)
    submitLoading.value = true
    try {
      const { success, data, retInfo } = await appTab_material_update_post({
        ...form,
        materialNo: originalRecord.materialNo,
        siteId: Number(ctx.siteId)
      })
      if (!isCurrent(ctx)) return
      if (!success) throw new Error(retInfo || '编辑物料失败')
      const responseRecord =
        data && typeof data === 'object' && !Array.isArray(data) ? data : {}
      const routeChanged =
        Number(form.jumpType) === 2 &&
        Number(form.routeId) !== Number(originalRecord.routeId)
      const updatedRecord = {
        ...originalRecord,
        ...form,
        ...responseRecord,
        materialNo: originalRecord.materialNo,
        materialTypeName:
          responseRecord.materialTypeName ||
          { 1: 'icon', 2: '大图', 3: 'icon+文字' }[form.materialType] ||
          originalRecord.materialTypeName,
        ...(routeChanged && !responseRecord.routeName
          ? { routeName: '', routeUrl: '' }
          : {})
      }
      updateSharedMaterial(draft.value, updatedRecord)
      modalVisible.value = false
      editingRecord.value = null
      message.success('编辑物料成功，已同步所有引用该物料的变体')
    } catch (error) {
      if (isCurrent(ctx)) message.error(error?.message || '编辑物料失败')
    } finally {
      if (isCurrent(ctx)) submitLoading.value = false
    }
  }

  watch(candidateModalVisible, visible => {
    if (!visible) candidateItems.value = []
  })
  watch(
    () => props.siteId,
    () => {
      generation++
      confirmation?.destroy()
      closeEditors()
      languagePromise = null
      languageSiteId.value = undefined
      languageOptions.value = []
      defaultLanguageCode.value = ''
      languageLoading.value = false
      publishModalVisible.value = false
      publishLoading.value = false
      submitLoading.value = false
      configLoading.value = false
      configReady.value = false
      configError.value = ''
      baselineLoading.value = false
      baselineError.value = ''
      expectedVersions.value = { 0: null, 1: null }
      validationError.value = null
      draft.value = createDraft()
      publishedDraft.value = cloneConfig(draft.value)
      publishedDetail.value = null
      activeVariantId.value = getDefaultVariant(draft.value).uiId
      if (props.siteId) {
        loadPublishedConfig()
        loadLanguageOptions()
      }
    },
    { immediate: true, flush: 'sync' }
  )
  onBeforeUnmount(() => {
    generation++
    candidateRequest++
    confirmation?.destroy()
  })

  return {
    activeVariant,
    activeVariantId,
    baselineError,
    baselineLoading,
    candidateItems,
    candidateLoading,
    candidateModalVisible,
    configError,
    configLoading,
    currentVersion,
    defaultLanguageCode,
    defaultMaterialNo,
    draft,
    editingRecord,
    editingVariant,
    experimentPanelRef,
    getMaterialDisplayText,
    getMaterialName,
    handleAddCandidates,
    handleExperimentField,
    handleExperimentToggle,
    handleOpenPublish,
    handlePreviewSelect,
    handlePublish,
    handleRemove,
    handleSetDefault,
    handleSubmit,
    handleVariantConfirm,
    interactionDisabled,
    items,
    languageOptions,
    loadOtherVersion: () => loadOtherVersion(),
    loadPublishedConfig,
    modalVisible,
    openCandidateModal,
    openEditModal,
    openVariantModal,
    previewMaterialNo,
    publishChanges,
    publishChecks,
    publishLoading,
    publishModalVisible,
    remainingSlots,
    removeVariant,
    selectVariant,
    submitLoading,
    validationError,
    variantModalVisible
  }
}
